import { FRAME_THEME_COLOR } from '@nandosaus/constants';
import { get, isEmpty, isUndefined, omitBy } from 'lodash';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React from 'react';

import { getFeatureFlags } from '@utils/feature-flags';
import { assetQuery, floodlightQuery, relatedEntriesQuery, seomaticQuery } from '../../content/query-snippets';
import { BackgroundFrame } from '../../components/frames/background-frame';
import { Button } from '../../components/button';
import { cmsProductPropTypes, floodlightPageLoadEventPropTypes } from '../../prop-types';
import { ContentMatrix } from '../../components/content-matrix';
import { ContentSubHeader, PageHeaderBlock } from '../../components/content-image';
import { extractSeoMetaFromEntry, seoPropDefaults, seoPropTypes } from '../../utils/extract-seo-meta-from-entry';
import { fetch } from '../../content/queries';
import { Flex } from '../../components/grid';
import { Layout } from '../../components/layout';
import { logger } from '../../utils/logger';
import { navigation } from '../../constants';
import { P } from '../../components/typography';
import { ProductDetails } from '../../components/product-details';
import { RelatedEntriesBlock } from '../../components/related-entries-block';
import { setCacheHeaders } from '../../utils/set-cache-headers';
import Error from '../_error';

const ProductPage = ({ contentMatrix, floodlightPageLoadEvent, metaContent, product, statusCode }) => {
  const { GREEN } = FRAME_THEME_COLOR;

  if (statusCode) {
    return <Error statusCode={statusCode} />;
  }

  const {
    additionalInformationTable,
    description,
    ingredients,
    nutritionalInformationTable,
    pageHeaderImage,
    relatedEntries,
    title,
  } = product;

  const breadcrumbs = {
    title,
    ancestors: [
      {
        title: 'Products',
        url: navigation.PRODUCTS,
      },
    ],
  };

  return (
    <Layout hasNavPadding title={title} metaContent={metaContent} floodlightPageLoadEvent={floodlightPageLoadEvent}>
      <PageHeaderBlock backgroundImage={pageHeaderImage} />
      <ContentSubHeader {...{ title, breadcrumbs }}>{description && <P>{description}</P>}</ContentSubHeader>
      <ContentMatrix hasExternalHeader blocks={contentMatrix} />
      {(additionalInformationTable || ingredients || nutritionalInformationTable) && (
        <BackgroundFrame mt="-5vw" showFireIcon variant={GREEN}>
          <ProductDetails
            additionalInformationTable={additionalInformationTable}
            body={ingredients}
            heading="Good to know stuff"
            nutritionalInformationTable={nutritionalInformationTable}
            subheading="Ingredients"
          />
        </BackgroundFrame>
      )}
      {!isEmpty(relatedEntries) && (
        <RelatedEntriesBlock heading="PERI-PERI inspired recipes" relatedEntries={relatedEntries}>
          <P width={{ xs: 1, md: 6 / 12 }} margin="auto" textAlign="center" py="1.5rem">
            Looking for some recipe inspiration? Breakfast, lunch, dinner or a snack, we’ve got you covered with some
            PERi-PERi inspired creations.
          </P>
          <Flex pt="1rem" pb="2rem">
            <Button href={navigation.RECIPES} mx="auto">
              View All
            </Button>
          </Flex>
        </RelatedEntriesBlock>
      )}
    </Layout>
  );
};

ProductPage.getInitialProps = async ({ query, res }) => {
  const preview = get(query, 'x-craft-live-preview');
  const token = get(query, 'token');
  const slug = get(query, 'slug', '');
  const featureFlags = await getFeatureFlags();

  const productsQuery = gql`
    query getProductFromCMS {
      entry(section: "products", slug: "${slug}", siteId: "${process.env.cms.siteId}") {
        ... on product_Entry {
          slug
          title
          description
          productCategory {
            title
          }
          ${assetQuery('pageHeaderImage')}
          nutritionalInformationTable {
            nutritionalInfo
            avgQtyPerServing
            avgQtyPer100g
          }
          ingredients
          additionalInformationTable {
            additionalInfo
            description
          }
          ${ContentMatrix.query}
          ${relatedEntriesQuery}
          ${floodlightQuery}
        }
        ${seomaticQuery(`nandos-at-home/${slug}`)}
      }
    }
  `;

  try {
    const data = await fetch({
      query: productsQuery,
      headers: omitBy({ 'x-craft-live-preview': preview, token }, isUndefined),
    });

    const product = get(data, 'entry', []);
    const floodlightPageLoadEvent = get(product, 'floodlightPageLoadEvent[0]');
    const metaContent = extractSeoMetaFromEntry(product);

    if (res) {
      if (!product) {
        // eslint-disable-next-line no-param-reassign
        res.statusCode = 404;
        return { statusCode: 404, featureFlags };
      }

      const isPreview = preview && token;
      setCacheHeaders({ res, isPreview });
    }

    return {
      product,
      contentMatrix: product.contentMatrix,
      metaContent,
      floodlightPageLoadEvent,
      featureFlags,
    };
  } catch (err) {
    logger.error('Failed to fetch product from CMS', err);
    return { statusCode: 500, featureFlags };
  }
};

ProductPage.propTypes = {
  contentMatrix: PropTypes.arrayOf(PropTypes.shape({})),
  floodlightPageLoadEvent: floodlightPageLoadEventPropTypes,
  metaContent: PropTypes.shape(seoPropTypes),
  product: cmsProductPropTypes.isRequired,
  statusCode: PropTypes.number,
};

ProductPage.defaultProps = {
  contentMatrix: undefined,
  floodlightPageLoadEvent: undefined,
  metaContent: seoPropDefaults,
  statusCode: null,
};

export default ProductPage;
